/**
 * KnowledgeProfileConnected — the knowledge-profile agent's Home detail-pane
 * app: the nurse's skills dashboard rendered IN the frame (like the Care
 * Planner board), not as a read-only summary. ONE dashboard, two homes:
 *   - full-screen in the knowledge-profile satellite app (KnowledgeProfileHome),
 *   - inline here in the main app's Home detail pane.
 * Both mount THIS component, so the two surfaces cannot drift.
 *
 * THIS AGENT IS NOT SESSION-SCOPED. The fold is the caller's own (`/me/skills`),
 * so this takes no props and ignores the detail-pane session: every
 * knowledge-profile session opens the same fold.
 *
 * What changed with adr/0025. The dashboard used to derive three views from one
 * attestation list (draft / published / changes-since-publish) and offered a
 * curator "refresh" that kicked an analysis pass. The fold is now the server's
 * whole answer: a skill carries its own `state` and `visibility`, there is no
 * diff to compute, and starting a curator pass is a run on the runs resource
 * rather than a verb on this surface.
 *
 * Resilience: all four async states are visible — ERROR (message + retry),
 * LOADING without cache (skeleton), EMPTY (meaningful), DATA. A failed read is
 * never rendered as an empty fold. Writes seed nothing optimistically: the
 * server's fold is the authority, the acted-on row shows a spinner, and a 412
 * refetches so she is looking at the current state rather than a toggle that
 * stayed where she left it.
 */

import { useState } from 'react'
import { RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@frontend/components/ui/button'
import { Skeleton } from '@frontend/components/ui/skeleton'
import { randomUUID } from '@frontend/lib/uuid'
import type { HeldSkill, OwnSkillVerb } from '@frontend/api/endpoints/skills'
import { useOwnSkillMutations, useOwnSkills } from '@frontend/hooks/useSkills'
import { KnowledgeMapView } from './KnowledgeMapView'
import { SelfDeclareForm } from './SelfDeclareForm'
import { SkillsListView } from './SkillsListView'
import { ViewSwitcher, type KnowledgeProfileView } from './ViewSwitcher'

function LoadingState() {
  const { t } = useTranslation()
  return (
    <div className="space-y-3" aria-busy="true" aria-label={t('skills.loading.ariaLabel')}>
      <Skeleton className="h-12 w-full rounded-full" />
      <Skeleton className="h-11 w-full rounded-full" />
      <Skeleton className="h-[88px] w-full rounded-[18px]" />
      <Skeleton className="h-[88px] w-full rounded-[18px]" />
      <Skeleton className="h-[88px] w-full rounded-[18px]" />
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-16 text-center">
      <p className="text-[17px] font-semibold text-[var(--color-surface-ink)]">
        {t('skills.error.loadFailed')}
      </p>
      <p className="text-[15px] text-[var(--color-surface-mute)]">{message}</p>
      <Button type="button" size="pill" onClick={onRetry} className="min-h-11">
        <RotateCw className="h-4 w-4" />
        {t('skills.error.retry')}
      </Button>
    </div>
  )
}

export function KnowledgeProfileConnected() {
  const { t } = useTranslation()
  const [view, setView] = useState<KnowledgeProfileView>('list')
  const { data, isLoading, isError, error, refetch } = useOwnSkills()
  const { declare, move } = useOwnSkillMutations()

  // Every verb carries the fold's own `version` as If-Match, so a decision the
  // nurse took against a view that has since moved fails loudly with a 412
  // instead of applying to a state she never saw.
  const runVerb = (verb: OwnSkillVerb) => (skill: HeldSkill) =>
    move.mutate({
      skillKey: skill.key,
      verb,
      version: skill.version,
      // Minted once per tap and held in `variables`, so TanStack's automatic
      // retry of the same attempt reuses it and the stream records once.
      idempotencyKey: randomUUID(),
    })

  // The key currently being written, which drives the per-row spinner. A write
  // resolves to a fresh fold, so the row simply re-renders without the spinner
  // once `isPending` clears.
  const busyKey = move.isPending ? move.variables.skillKey : undefined

  const skills = data?.items ?? []

  return (
    <div className="h-full overflow-auto bg-[var(--color-surface-canvas,#ffffff)]">
      <div className="mx-auto w-full max-w-3xl space-y-8 px-4 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        {/* Always on, even mid-error, so she is never blocked from recording
            what she has worked with. */}
        <section aria-label={t('skills.section.declare')}>
          <SelfDeclareForm
            onDeclare={(request) => declare.mutateAsync({ request, idempotencyKey: randomUUID() })}
            busy={declare.isPending}
          />
        </section>

        {isError && !data ? (
          <ErrorState
            message={error?.message ?? t('skills.error.unknownError')}
            onRetry={() => refetch()}
          />
        ) : isLoading && !data ? (
          <LoadingState />
        ) : data ? (
          <section aria-label={t('skills.section.view')}>
            <ViewSwitcher value={view} onChange={setView} />
            <div className="mt-5">
              {view === 'list' ? (
                <SkillsListView
                  skills={skills}
                  onAccept={runVerb('accept')}
                  onReject={runVerb('reject')}
                  onPublish={runVerb('publish')}
                  onUnpublish={runVerb('unpublish')}
                  busyKey={busyKey}
                />
              ) : (
                <KnowledgeMapView skills={skills} />
              )}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  )
}

export default KnowledgeProfileConnected
